export const COST_GROWTH = 1.15;
export const BASE_PER_CORRECT = 1;

export const OFFLINE_CAP_HOURS = { free: 2, premium: 24 };

// Guest -> account nudge
export const SIGNUP_PROMPT = { coins: 1000, helpers: 3 };

// Gems come only from finishing a full Scales session.
export const GEMS_PER_SESSION = 1;
export const GEMS_BONUS_ACCURACY = 0.9; // finish at or above this and the session pays double
export const gemsForSession = (accuracy = 1) =>
  accuracy >= GEMS_BONUS_ACCURACY ? GEMS_PER_SESSION * 2 : GEMS_PER_SESSION;

// Exactly six helpers. premium: true = locked for free users.
export const BUILDINGS = [
  {
    id: 'metronome', name: 'Metronome', baseCost: 15, cps: 0.1, premium: false,
    artSize: 46, maxShown: 8, blurb: 'Keeps time passively.',
  },
  {
    id: 'stand', name: 'Music Stand', baseCost: 100, cps: 1, premium: false,
    artSize: 56, maxShown: 6, blurb: 'Holds sheet music up.',
  },
  {
    id: 'principal', name: 'Principal Chair Player', baseCost: 12000, cps: 47, premium: false,
    artSize: 84, maxShown: 5, blurb: 'Takes all the solos.',
  },
  {
    id: 'conductor', name: 'Conductor', baseCost: 130000, cps: 260, premium: true,
    artSize: 66, maxShown: 4, blurb: 'Waves a baton to keep everyone together.',
  },
  {
    id: 'hall', name: 'Metropolitan Symphony Hall', baseCost: 1400000, cps: 1400, premium: true,
    artSize: 120, maxShown: 3, blurb: 'Grand acoustic venue for massive crowds.',
  },
  {
    id: 'fork', name: 'Tuning Fork Synthesizer', baseCost: 7500000, cps: 5000, premium: true,
    artSize: 52, maxShown: 3, blurb: 'Auto-tunes notes into pure currency.',
  },
];

/* ---------- Coin upgrades ---------- */
// Upgrade PNGs are bundled from src/assets/upgrades.
// The image field is a URL generated by the bundler.
const UPGRADE_IMAGES = require.context("../../assets/upgrades", false, /\.png$/);
export const upgradeImage = (id) => {
  const key = `./${id}.png`;
  try {
    return UPGRADE_IMAGES(key);
  } catch {
    return null;
  }
};
const REQUIREMENTS = [1, 5, 25, 50, 100, 150];
const COST_MULTIPLIERS = [10, 50, 500, 5000, 50000, 500000];
// Cost multipliers for tiers 5–6 extend the existing progression; adjust for balancing.
const BUILDING_UPGRADE_DATA = {
  metronome: [
    ["WD-40 for Cogs", "Less squeaking, more ticking."],
    ["Overclocked Springs", "Now ticking at a modest 4,000 BPM."],
    ["Precious Metal Cogs", "Heavy metal, literally."],
    ["Quantum Pendulums", "Ticks here and there simultaneously."],
    ["Tachyonic Escapements", "Ticking before the tempo even started."],
    ["Self-Aware Chronometers", "It judges your sense of rhythm."],
  ],
  stand: [
    ["Extra Large Clothes-Pins", "Outdoor gig proof."],
    ["Laminated Parts", "Spill-proof against tearful practice sessions."],
    ["Heavyweight Cardstock", "Thicker than your band director’s patience."],
    ["Enchanted Manuscripts", "The pages turn themselves when you're not looking."],
    ["Infinite Folders", "Holds every arrangement ever written, plus snacks."],
    ["Telepathic Sheet Music", "Projecting the notes straight into your mind."],
  ],
  principal: [
    ["Extra-Shiny Polish", "Blinds the first row so they can’t see missed notes."],
    ["Bach 3C Masterpiece", "Every trumpet player’s secret weapon."],
    ["Lead-Pipe Screamer", "Can play an octave above human hearing."],
    ["Ego-Inflating Booster", "Yes, everyone WAS listening to your solo."],
    ["24k Gold Rim", "Pure flex for the front row."],
    ["Liquid-Cooled Valves", "To prevent spontaneous combustion during 16th-note runs."],
  ],
  conductor: [
    ["Crisp Cotton Gloves", "Squeaky clean cues."],
    ["Ergonomic Baton Grips", "No more flying batons into the oboe section."],
    ["White Silk Gloves", "Feather-light dynamics."],
    ["Intimidating Stares", "One look and the brass section plays pianissimo."],
    ["Hypnotic Baton Waves", "You are getting very sleepy... and playing in time."],
    ["Telekinetic Gloves", "Conducting without moving a muscle."],
  ],
  hall: [
    ["Padded Folding Seats", "Slightly less numb bottoms."],
    ["Velvet Orchestra Chairs", "Fancy enough to charge double for tickets."],
    ["Acoustic Sound-Baffles", "Makes even wrong notes sound intentional."],
    ["VIP Box Memory Foam", "Donor-approved comfort."],
    ["Zero-Gravity Loungers", "Float away on a cloud of brass harmony."],
    ["Resonance-Amplified Dome", "Sound travels straight to neighboring dimensions."],
  ],
  fork: [
    ["A440 Calibrators", "Perfect pitch, guaranteed."],
    ["Resonant Alloy Prongs", "Vibrates long enough to boil a cup of tea."],
    ["Oscilloscope Displays", "Ooooh, look at those shiny green waves!"],
    ["Sub-Atomic Generators", "Tuning the fundamental particles of reality."],
    ["Harmonic Quartz Arrays", "Purifying tone at the speed of light."],
    ["Dimensional Resonators", "Singing in frequencies that unlock hidden universe doors."],
  ],
};
// Metronome upgrades are regular building upgrades, with an additional
// coins-per-correct multiplier applied in computePerCorrect below.
export const buildingUpgrades = BUILDINGS.flatMap((b) =>
  BUILDING_UPGRADE_DATA[b.id].map(([name, desc], i) => ({
    id: `${b.id}-${i}`, kind: 'building', currency: 'coins', building: b.id,
    name, desc, cost: b.baseCost * COST_MULTIPLIERS[i], req: REQUIREMENTS[i],
    value: 2, image: upgradeImage(`${b.id}-${i}`),
  })),
);

/* ---------- Gem upgrades: multipliers, rare passive generators, cosmetics ---------- */
export const GEM_UPGRADES = [
  { id: 'gem-rosin', kind: 'multiplier', value: 1.5, name: 'Golden Rosin', desc: 'Every helper makes 1.5× as many coins.', cost: 10 },
  { id: 'gem-pitch', kind: 'multiplier', value: 2, name: 'Concert Pitch', desc: 'Every helper makes twice as many coins.', cost: 40 },
  { id: 'gem-blessing', kind: 'multiplier', value: 3, name: "Maestro's Blessing", desc: 'Every helper makes three times as many coins.', cost: 150 },
  { id: 'gem-embouchure', kind: 'click', value: 3, name: 'Perfect Embouchure', desc: 'Right answers earn three times the coins.', cost: 25 },
  { id: 'gem-ghost', kind: 'passive', value: 100, name: 'Ghost of a Great Soloist', desc: 'Haunts the band room. +100 coins a second.', cost: 20 },
  { id: 'gem-echo', kind: 'passive', value: 1000, name: 'The Practice Room Echo', desc: 'Never stops practicing. +1,000 coins a second.', cost: 80 },
  { id: 'theme-blueprint', kind: 'theme', value: 'blueprint', name: 'Blueprint Paper', desc: 'Swap the band room paper for blueprint blue.', cost: 15 },
  { id: 'theme-chalkboard', kind: 'theme', value: 'chalkboard', name: 'Chalkboard', desc: 'A green chalkboard for late-night practice.', cost: 15 },
].map((u) => ({ ...u, currency: 'gems' }));

export const UPGRADES = [...buildingUpgrades, ...GEM_UPGRADES];

/* ---------- Math ---------- */
export const isUnlocked = (u, s) => {
  if (u.building) return (s.owned[u.building] || 0) >= u.req;
  if (u.currency === 'coins') return s.correct >= u.req;
  return true; // gem upgrades are always on the shelf
};

export const buildingCost = (b, owned, n = 1) =>
  Math.ceil(
    (b.baseCost * Math.pow(COST_GROWTH, owned) * (Math.pow(COST_GROWTH, n) - 1)) /
      (COST_GROWTH - 1),
  );

export const buildingMult = (id, bought) =>
  UPGRADES.reduce(
    (m, u) => (u.building === id && bought[u.id] ? m * u.value : m),
    1,
  );

export const globalMult = (bought) =>
  UPGRADES.reduce((m, u) => (u.kind === 'multiplier' && bought[u.id] ? m * u.value : m), 1);

export const flatPassive = (bought) =>
  UPGRADES.reduce((n, u) => (u.kind === 'passive' && bought[u.id] ? n + u.value : n), 0);

// coins/sec one copy of this helper makes right now
export const eachRate = (b, bought) => b.cps * buildingMult(b.id, bought) * globalMult(bought);

export const computeCps = (owned, bought) =>
  BUILDINGS.reduce((sum, b) => sum + (owned[b.id] || 0) * b.cps * buildingMult(b.id, bought), 0) *
    globalMult(bought) +
  flatPassive(bought);

// Each purchased metronome upgrade doubles coins earned per correct answer,
// in addition to doubling metronome production via buildingMult.
// Gem click bonuses (such as Perfect Embouchure) still work independently.
export const computePerCorrect = (bought) =>
  UPGRADES.reduce((v, u) => {
    if (!bought[u.id]) return v;
    if (u.building === 'metronome' || u.kind === 'click') return v * u.value;
    return v;
  }, BASE_PER_CORRECT);

/* ---------- Achievements (everything here only goes up, so no extra state) ---------- */
const firstOf = (id, name, desc) => ({
  id: `first-${id}`,
  name,
  desc,
  test: (s) => (s.owned[id] || 0) >= 1,
});

export const ACHIEVEMENTS = [
  { id: 'right-1', name: 'First Note', desc: 'Get one card right.', test: (s) => s.correct >= 1 },
  { id: 'right-10', name: 'Warming Up', desc: 'Get 10 cards right.', test: (s) => s.correct >= 10 },
  { id: 'right-100', name: 'Getting Somewhere', desc: 'Get 100 cards right.', test: (s) => s.correct >= 100 },
  { id: 'right-1000', name: 'Band Room Regular', desc: 'Get 1,000 cards right.', test: (s) => s.correct >= 1000 },
  { id: 'coins-100', name: 'Pocket Change', desc: 'Earn 100 coins in total.', test: (s) => s.totalCoins >= 100 },
  { id: 'coins-10k', name: 'Piggy Bank', desc: 'Earn 10,000 coins in total.', test: (s) => s.totalCoins >= 10000 },
  { id: 'coins-1m', name: 'Rainy Day Fund', desc: 'Earn 1,000,000 coins in total.', test: (s) => s.totalCoins >= 1000000 },
  { id: 'cps-1', name: 'Slow and Steady', desc: 'Make 1 coin a second.', test: (s) => s.cps >= 1 },
  { id: 'cps-100', name: 'Busy Little Band', desc: 'Make 100 coins a second.', test: (s) => s.cps >= 100 },
  { id: 'gem-1', name: 'Something Shiny', desc: 'Earn your first gem.', test: (s) => s.totalGems >= 1 },
  firstOf('metronome', 'Tick Tock', 'Hire a metronome.'),
  firstOf('stand', 'Part Holder', 'Hire a music stand.'),
  firstOf('principal', 'First Chair', 'Hire a principal chair player.'),
  firstOf('conductor', 'Maestro', 'Hire a conductor.'),
  firstOf('hall', 'Packed House', 'Book the symphony hall.'),
  firstOf('fork', 'In Tune', 'Build a tuning fork synthesizer.'),
];